import { OfferRejectionCode, type CreditOffer } from '@/features/offer/types';
import type { ApplicationState } from '@/features/application/types/application';
import type { VoltTier } from '@/features/offer/components/VoltTierFace/VoltTierFace';

// A missing `isFinal` counts as final: only an explicit false marks a
// preliminary offer, which cannot be applied for.
const isFinalOffer = (offer: CreditOffer) => offer.isFinal !== false;

const isUsableOffer = (offer: CreditOffer) =>
  offer.isAvailable && offer.creditLimit > 0;

export const hasAvailableOffer = (offers: CreditOffer[] | undefined) => {
  return (
    offers?.some((offer) => isFinalOffer(offer) && isUsableOffer(offer)) ??
    false
  );
};

// The lender's final offer, still valid, whether or not it answers an offer
// request at Volt: users who applied at Ocean directly have none at Volt.
export const hasCurrentFinalOffer = (
  application:
    | Pick<ApplicationState, 'hasFinalOffer' | 'hasOffersExpired'>
    | undefined
) => !!application?.hasFinalOffer && !application.hasOffersExpired;

const VOLT_TIERS: Record<string, VoltTier> = {
  volt_zero: 'zero',
  volt_one: 'one',
  volt_infinite: 'infinite',
};

export interface VoltAlternative {
  id: string;
  tier: VoltTier;
  amount: number;
}

// The Volt products Ocean offers instead of a credit, bought in the app.
export const getVoltAlternatives = (
  offers: CreditOffer[] | undefined
): VoltAlternative[] =>
  (offers ?? []).flatMap((offer) => {
    const tier = VOLT_TIERS[offer.subProductName];
    return tier && isUsableOffer(offer)
      ? [{ id: offer.id, tier, amount: offer.creditLimit }]
      : [];
  });

export const getPreliminaryOffers = (
  offers: CreditOffer[] | undefined
): CreditOffer[] =>
  offers?.filter((offer) => !isFinalOffer(offer) && isUsableOffer(offer)) ??
  [];

// Rejections the user can act on with a proof of income.
const INCOME_REJECTION_CODES = new Set<string>([
  OfferRejectionCode.IncomeBelowMinimum,
  OfferRejectionCode.IncomeNotVerified,
  OfferRejectionCode.DtiRatioExceeded,
]);

export const hasPreliminaryIncomeRejection = (
  offers: CreditOffer[] | undefined
): boolean =>
  offers?.some(
    (offer) =>
      !isFinalOffer(offer) &&
      !!offer.rejectedCode &&
      INCOME_REJECTION_CODES.has(offer.rejectedCode)
  ) ?? false;
